<script setup lang="ts">
import {
    ArrowDown,
    ArrowUp,
    Loader2,
    Pencil,
    Plus,
    Save,
    ShieldX,
    Trash2,
    X
} from 'lucide-vue-next';
import { computed, inject, ref, watch } from 'vue';

import ConfirmDialog from '@/components/ConfirmDialog.vue';
import AiModelCatalogTable from '@/components/settingsMenu/sections/AiModelCatalogTable.vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import {
    type AiModel,
    createAiModelInCatalog,
    deleteAiModelFromCatalog,
    getAiModelCatalog,
    updateAiModelInCatalog,
    type UpdateAiModelPayload
} from '@/services/ai_models';
import { useModelStore } from '@/stores/modelStore';
import { useTenantStore } from '@/stores/tenant';

type KeyValueEntry = {
    key: string;
    value: string;
};

type FormState = {
    model_id: string;
    provider: string;
    model_label: string;
    input_tokens_price: number;
    output_token_price: number;
    params: KeyValueEntry[];
};

const tenantStore = useTenantStore();
const modelStore = useModelStore();
const refreshHasAiModels = inject<() => Promise<void>>('refreshHasAiModels');
const providerOptions = [
    { label: 'Azure Open AI', value: 'openai' },
    { label: 'Google', value: 'google' },
    { label: 'Anthropic', value: 'anthropic' },
    { label: 'Mistral', value: 'mistral' }
];

const models = ref<AiModel[]>([]);
const loading = ref(false);
const saving = ref(false);
const error = ref<string | null>(null);
const loadingMove = ref(false);

const isFormDialogOpen = ref(false);
const isDeleteDialogOpen = ref(false);
const editingModel = ref<AiModel | null>(null);
const modelToDelete = ref<AiModel | null>(null);

const form = ref<FormState>(emptyForm());

const canManage = computed(
    () => tenantStore.tenantElqano && tenantStore.tenantAdmin
);
const isEditMode = computed(() => editingModel.value !== null);
const tenantId = computed(() => tenantStore.tenantId || '');

const isFormValid = computed(() => {
    if (!form.value.model_id.trim() && !isEditMode.value) return false;
    if (!form.value.provider.trim()) return false;
    if (!form.value.model_label.trim()) return false;
    if (Number.isNaN(form.value.input_tokens_price)) return false;
    if (Number.isNaN(form.value.output_token_price)) return false;
    if (
        form.value.input_tokens_price < 0 ||
        form.value.output_token_price < 0
    ) {
        return false;
    }
    return true;
});

function emptyForm(): FormState {
    return {
        model_id: '',
        provider: 'openai',
        model_label: '',
        input_tokens_price: 0,
        output_token_price: 0,
        params: [{ key: '', value: '' }]
    };
}

function objectToEntries(
    value: Record<string, string> | null | undefined
): KeyValueEntry[] {
    const entries = Object.entries(value || {}).map(([key, val]) => ({
        key,
        value: String(val)
    }));
    return entries.length > 0 ? entries : [{ key: '', value: '' }];
}

function entriesToObject(entries: KeyValueEntry[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const entry of entries) {
        const key = entry.key.trim();
        if (!key) continue;
        out[key] = entry.value;
    }
    return out;
}

function resetForm(model: AiModel | null = null) {
    if (!model) {
        form.value = emptyForm();
        return;
    }
    form.value = {
        model_id: model.model_id,
        provider: model.provider,
        model_label: model.model_label,
        input_tokens_price: model.input_tokens_price,
        output_token_price: model.output_token_price,
        params: objectToEntries(model.params)
    };
}

async function loadModels() {
    const currentTenantId = tenantId.value;
    if (!currentTenantId || !canManage.value) return;

    loading.value = true;
    error.value = null;
    try {
        models.value = await getAiModelCatalog(currentTenantId);
    } catch (err) {
        console.error(err);
        error.value = 'Failed to load AI models.';
    } finally {
        loading.value = false;
    }
}

function openCreateDialog() {
    editingModel.value = null;
    resetForm();
    isFormDialogOpen.value = true;
}

function openEditDialog(model: AiModel) {
    editingModel.value = model;
    resetForm(model);
    isFormDialogOpen.value = true;
}

function openDeleteDialog(model: AiModel) {
    modelToDelete.value = model;
    isDeleteDialogOpen.value = true;
}

function addParam() {
    form.value.params.push({ key: '', value: '' });
}

function removeParam(index: number) {
    if (form.value.params.length === 1) {
        form.value.params[0] = { key: '', value: '' };
        return;
    }
    form.value.params.splice(index, 1);
}

async function submitForm() {
    if (!isFormValid.value || !tenantId.value) return;

    saving.value = true;
    error.value = null;
    try {
        const payload: AiModel = {
            tenant_id: tenantId.value,
            model_id: form.value.model_id.trim(),
            provider: form.value.provider.trim(),
            model_label: form.value.model_label.trim(),
            params: entriesToObject(form.value.params),
            input_tokens_price: form.value.input_tokens_price,
            output_token_price: form.value.output_token_price
        };

        if (isEditMode.value && editingModel.value) {
            const updatePayload: UpdateAiModelPayload = {
                tenant_id: tenantId.value,
                provider: payload.provider,
                model_label: payload.model_label,
                params: payload.params,
                input_tokens_price: payload.input_tokens_price,
                output_token_price: payload.output_token_price
            };
            await updateAiModelInCatalog(
                editingModel.value.model_id,
                updatePayload
            );
        } else {
            await createAiModelInCatalog(payload);
        }

        isFormDialogOpen.value = false;
        await loadModels();
        await Promise.all([
            modelStore.loadAllowedModels(),
            refreshHasAiModels?.()
        ]);
    } catch (err) {
        console.error(err);
        error.value = 'Failed to save AI model.';
    } finally {
        saving.value = false;
    }
}

async function confirmDelete() {
    if (!tenantId.value || !modelToDelete.value) return;
    error.value = null;
    try {
        await deleteAiModelFromCatalog(
            tenantId.value,
            modelToDelete.value.model_id
        );
        modelToDelete.value = null;
        await loadModels();
        await Promise.all([
            modelStore.loadAllowedModels(),
            refreshHasAiModels?.()
        ]);
    } catch (err) {
        console.error(err);
        error.value = 'Failed to delete AI model.';
    }
}

async function moveModel(model: AiModel, direction: 'up' | 'down') {
    try {
        loadingMove.value = true;
        const updatedModel = await updateAiModelInCatalog(model.model_id, {
            tenant_id: tenantId.value,
            reorder_to: direction
        });
        if (model.order === updatedModel.order) {
            return;
        }

        const idx = models.value.findIndex(
            (m) => m.model_id === model.model_id
        );
        if (idx === -1) return;

        const swapWith = direction === 'up' ? idx - 1 : idx + 1;
        if (swapWith < 0 || swapWith >= models.value.length) return;

        const neighbor = models.value[swapWith];
        const reordered = [...models.value];
        reordered[idx] = { ...neighbor, order: model.order };
        reordered[swapWith] = { ...model, order: updatedModel.order };
        models.value = reordered;
    } catch (err) {
        console.error(err);
        error.value = 'Failed to move AI model.';
    } finally {
        loadingMove.value = false;
    }
}

watch(
    () => [
        tenantStore.tenantId,
        tenantStore.tenantElqano,
        tenantStore.tenantAdmin
    ],
    () => {
        loadModels();
    },
    { immediate: true }
);
</script>

<template>
    <div class="title flex flex-col gap-6">
        <div class="flex items-start justify-between gap-4">
            <div>
                <h2 class="text-xl font-semibold">AI model management</h2>
                <p class="text-sm text-muted-foreground mt-1">
                    Manage global AI models available to Playground.
                </p>
            </div>
            <Button v-if="canManage" @click="openCreateDialog">
                <Plus class="h-4 w-4 mr-2" />
                Add AI Model
            </Button>
        </div>

        <div
            v-if="!canManage"
            class="border rounded-xl p-6 flex items-center gap-3 text-muted-foreground"
        >
            <ShieldX class="h-5 w-5" />
            This section is available only to Elqano tenant admins.
        </div>

        <div
            v-else-if="error"
            class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
        >
            <p class="text-sm text-destructive">{{ error }}</p>
        </div>

        <AiModelCatalogTable
            v-if="canManage"
            :models="models"
            :loading="loading"
            :loading-update-order="loadingMove"
        >
            <template #actions="{ model }">
                <div class="inline-flex gap-2">
                    <Button
                        variant="outline"
                        size="icon-sm"
                        @click="moveModel(model, 'up')"
                    >
                        <ArrowUp class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        @click="moveModel(model, 'down')"
                    >
                        <ArrowDown class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        @click="openEditDialog(model)"
                    >
                        <Pencil class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="destructive"
                        size="icon-sm"
                        @click="openDeleteDialog(model)"
                    >
                        <Trash2 class="h-4 w-4" />
                    </Button>
                </div>
            </template>
        </AiModelCatalogTable>
    </div>

    <Dialog v-model:open="isFormDialogOpen">
        <DialogContent class="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle>
                    {{ isEditMode ? 'Edit AI Model' : 'Create AI Model' }}
                </DialogTitle>
                <DialogDescription>
                    Configure model metadata, pricing and dynamic runtime
                    settings.
                </DialogDescription>
            </DialogHeader>

            <form class="space-y-6" @submit.prevent="submitForm">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                        <Label for="model_id">Model ID</Label>
                        <Input
                            id="model_id"
                            v-model="form.model_id"
                            :disabled="isEditMode"
                            placeholder="e.g. gpt-4.1-mini"
                        />
                    </div>
                    <div class="space-y-2">
                        <Label for="provider">Provider</Label>
                        <Select v-model="form.provider">
                            <SelectTrigger id="provider">
                                <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem
                                    v-for="provider in providerOptions"
                                    :key="provider.value"
                                    :value="provider.value"
                                >
                                    {{ provider.label }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div class="space-y-2">
                        <Label for="input_tokens_price"
                            >Input Tokens Price ($/1M)</Label
                        >
                        <Input
                            id="input_tokens_price"
                            v-model.number="form.input_tokens_price"
                            type="number"
                            min="0"
                            step="0.000001"
                        />
                    </div>
                    <div class="space-y-2">
                        <Label for="output_token_price"
                            >Output Tokens Price ($/1M)</Label
                        >
                        <Input
                            id="output_token_price"
                            v-model.number="form.output_token_price"
                            type="number"
                            min="0"
                            step="0.000001"
                        />
                    </div>
                    <div class="space-y-2">
                        <Label for="model_label">Model Label</Label>
                        <Input
                            id="model_label"
                            v-model="form.model_label"
                            placeholder="e.g. GPT 4.1 Mini"
                            required
                        />
                    </div>
                </div>

                <div class="space-y-3">
                    <div class="flex items-center justify-between">
                        <h3 class="font-medium">Params</h3>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            @click="addParam()"
                        >
                            <Plus class="h-4 w-4 mr-1" />
                            Add param
                        </Button>
                    </div>
                    <div
                        v-for="(entry, index) in form.params"
                        :key="`param-${index}`"
                        class="grid grid-cols-[1fr_1fr_auto] gap-2"
                    >
                        <Input v-model="entry.key" placeholder="key" />
                        <Input v-model="entry.value" placeholder="value" />
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            @click="removeParam(index)"
                        >
                            <X class="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <div class="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        @click="isFormDialogOpen = false"
                    >
                        Cancel
                    </Button>
                    <Button type="submit" :disabled="saving || !isFormValid">
                        <Loader2
                            v-if="saving"
                            class="h-4 w-4 mr-2 animate-spin"
                        />
                        <Save v-else class="h-4 w-4 mr-2" />
                        {{ isEditMode ? 'Save changes' : 'Create model' }}
                    </Button>
                </div>
            </form>
        </DialogContent>
    </Dialog>

    <ConfirmDialog
        v-model:is-confirm-dialog-open="isDeleteDialogOpen"
        title="Delete AI Model"
        :description="`Are you sure you want to delete ${modelToDelete?.model_id}?`"
        confirm-button-text="Delete"
        @confirm="confirmDelete"
    />
</template>
